import { useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { DocstateProvider, useDocstate } from "@/context/DocstateContext";
import { DS_BASE } from "@/lib/docstate";
import { trackVisit } from "@/lib/brand";
import { ShortlistProvider } from "@/context/ShortlistContext";
import { DocstateHeader } from "@/components/docstate/DocstateHeader";
import { DocstateFooter } from "@/components/docstate/DocstateFooter";
import { ChatWidget } from "@/components/docstate/ChatWidget";
import { WhatsAppFab } from "@/components/docstate/WhatsAppFab";
import DocstateHome from "./DocstateHome";
import DocstateListings from "./DocstateListings";
import DocstateProperty from "./DocstateProperty";
import DocstateAgents from "./DocstateAgents";
import DocstateListWithUs from "./DocstateListWithUs";
import DocstatePage from "./DocstatePage";
import DocstateContact from "./DocstateContact";
import DocstateCareers from "./DocstateCareers";
import DocstateBlog from "./DocstateBlog";
import DocstatePost from "./DocstatePost";
import DocstateGuides from "./DocstateGuides";
import DocstateGuide from "./DocstateGuide";
import DocstateMedia from "./DocstateMedia";
import DocstateShortlist from "./DocstateShortlist";
import DocstateOffPlan from "./DocstateOffPlan";

// Per-route SEO titles (SEO/AEO-friendly, Dubai-focused keywords).
const TITLES = {
  "": "Dubai Real Estate — Luxury Properties for Sale & Rent",
  buy: "Properties for Sale in Dubai",
  rent: "Properties for Rent in Dubai",
  properties: "All Properties in Dubai",
  commercial: "Commercial Property in Dubai",
  "holiday-homes": "Dubai Holiday Homes & Short-Term Rentals",
  "off-plan": "Off-Plan Properties & New Developments in Dubai",
  sell: "Sell Your Property in Dubai",
  "property-management": "Property Management in Dubai",
  about: "About Docstate Properties",
  team: "Meet Our Dubai Property Advisors",
  agents: "Meet Our Dubai Property Advisors",
  careers: "Careers at Docstate Properties",
  blog: "Dubai Property Insights & Journal",
  "area-guides": "Dubai Community & Area Guides",
  videos: "Dubai Property Videos & Tours",
  podcasts: "The Docstate Property Podcast",
  contact: "Contact Docstate Properties",
  "list-with-us": "List With Us — Partner With Docstate",
};

const DocstateSEO = () => {
  const data = useDocstate();
  const location = useLocation();
  useEffect(() => { trackVisit("docstate"); }, [location.pathname]);
  useEffect(() => {
    const s = data?.settings || {};
    const brand = s.name || "Docstate Properties";
    let path = location.pathname;
    if (DS_BASE && path.startsWith(DS_BASE)) path = path.slice(DS_BASE.length);
    const seg = path.replace(/^\//, "").split("/")[0];
    const sub = TITLES[seg] || TITLES[""];
    document.title = `${sub} | ${brand}`;
    const setMeta = (sel, attr, key, val) => {
      let el = document.querySelector(sel);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    const desc = s.tagline || "Curated luxury residences, off-plan investments and commercial assets across Dubai's most sought-after communities.";
    setMeta('meta[name="description"]', "name", "description", desc);
    setMeta('meta[property="og:title"]', "property", "og:title", document.title);
    setMeta('meta[property="og:description"]', "property", "og:description", desc);
    setMeta('meta[property="og:type"]', "property", "og:type", "website");
    if (s.logo_url) setMeta('meta[property="og:image"]', "property", "og:image", `${process.env.REACT_APP_BACKEND_URL}${s.logo_url}`);
    // canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = window.location.href;
    // favicon — Docstate's own gold mark (overrides the clinic default)
    let icon = document.querySelector("link[rel~='icon']");
    if (!icon) { icon = document.createElement("link"); icon.rel = "icon"; document.head.appendChild(icon); }
    icon.href = s.favicon_url || "/assets/docstate-mark.png";
    // JSON-LD RealEstateAgent org schema
    let ld = document.getElementById("docstate-jsonld");
    if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "docstate-jsonld"; document.head.appendChild(ld); }
    ld.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "RealEstateAgent", name: brand,
      description: desc, areaServed: "Dubai, United Arab Emirates",
      telephone: s.phone || undefined, email: s.email || undefined,
      address: { "@type": "PostalAddress", addressLocality: "Dubai", addressCountry: "AE" },
    });
  }, [data, location.pathname]);
  return null;
};

const Inner = () => {
  const data = useDocstate();
  const light = (data?.settings?.theme || "dark") === "light";
  return (
  <div className={`ds-root min-h-screen ${light ? "ds-theme-light" : ""}`}>
    <DocstateSEO />
    <DocstateHeader />
    <main>
      <Routes>
        <Route index element={<DocstateHome />} />
        <Route path="properties" element={<DocstateListings />} />
        <Route path="buy" element={<DocstateListings preset={{ purpose: "sale", lockPurpose: true, eyebrow: "Buy in Dubai", title: "Properties for Sale", subtitle: "Own a home or investment in Dubai's most desirable communities." }} />} />
        <Route path="rent" element={<DocstateListings preset={{ purpose: "rent", lockPurpose: true, eyebrow: "Rent in Dubai", title: "Properties for Rent", subtitle: "Premium long-term rentals across the city." }} />} />
        <Route path="commercial" element={<DocstateListings preset={{ purpose: "commercial", lockPurpose: true, eyebrow: "Commercial", title: "Commercial Property", subtitle: "Offices, retail and investment assets in Dubai's business districts." }} />} />
        <Route path="holiday-homes" element={<DocstateListings preset={{ holiday: true, eyebrow: "Holiday Homes", title: "Holiday Homes", subtitle: "Fully-managed short-stay residences for guests and owners." }} />} />
        <Route path="off-plan" element={<DocstateOffPlan />} />
        <Route path="property/:id" element={<DocstateProperty />} />
        <Route path="sell" element={<DocstatePage slug="sell" withEnquiry enquiryKind="sell" enquiryHeading="Request a free valuation" />} />
        <Route path="property-management" element={<DocstatePage slug="property-management" withEnquiry enquiryKind="property_management" enquiryHeading="Enquire about management" />} />
        <Route path="about" element={<DocstatePage slug="about" />} />
        <Route path="team" element={<DocstateAgents />} />
        <Route path="agents" element={<DocstateAgents />} />
        <Route path="careers" element={<DocstateCareers />} />
        <Route path="blog" element={<DocstateBlog />} />
        <Route path="blog/:slug" element={<DocstatePost />} />
        <Route path="area-guides" element={<DocstateGuides />} />
        <Route path="area-guides/:slug" element={<DocstateGuide />} />
        <Route path="videos" element={<DocstateMedia kind="video" />} />
        <Route path="podcasts" element={<DocstateMedia kind="podcast" />} />
        <Route path="contact" element={<DocstateContact />} />
        <Route path="shortlist" element={<DocstateShortlist />} />
        <Route path="list-with-us" element={<DocstateListWithUs />} />
        <Route path="*" element={<DocstateHome />} />
      </Routes>
    </main>
    <DocstateFooter />
    <ChatWidget />
      <WhatsAppFab />
  </div>
  );
};

export default function DocstatePortal() {
  return (
    <DocstateProvider>
      <ShortlistProvider>
        <Inner />
      </ShortlistProvider>
    </DocstateProvider>
  );
}
